import type { CobranzaFilter, CobranzaSummary } from '@yawi/types';
import { AlertCircle, Ban, CheckCircle2, Wallet } from 'lucide-react';
import React from 'react';
import { formatCurrency } from '../utils/format';

interface CobranzaSummaryCardsProps {
  summary: CobranzaSummary | null;
  loading: boolean;
  activeFilter: CobranzaFilter;
  onFilterChange: (filter: CobranzaFilter) => void;
}

interface CardDef {
  key: string;
  filter: CobranzaFilter | null; // null = tarjeta informativa, no es filtro
  label: string;
  value: string;
  icon: React.ReactNode;
  accent: string;
}

export const CobranzaSummaryCards: React.FC<CobranzaSummaryCardsProps> = ({
  summary,
  loading,
  activeFilter,
  onFilterChange,
}) => {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    );
  }

  const cards: CardDef[] = [
    {
      key: 'withDebt',
      filter: 'withDebt',
      label: 'Con adeudo',
      value: String(summary.withDebt),
      icon: <AlertCircle size={20} />,
      accent: 'text-amber-600 bg-amber-50',
    },
    {
      key: 'blocked',
      filter: 'blocked',
      label: 'Bloqueados',
      value: String(summary.blocked),
      icon: <Ban size={20} />,
      accent: 'text-red-600 bg-red-50',
    },
    {
      key: 'current',
      filter: 'current',
      label: 'Al día',
      value: String(summary.current),
      icon: <CheckCircle2 size={20} />,
      accent: 'text-emerald-600 bg-emerald-50',
    },
    {
      key: 'totalOwed',
      filter: null,
      label: 'Total por cobrar',
      value: formatCurrency(summary.totalOwed),
      icon: <Wallet size={20} />,
      accent: 'text-[#7C5CFC] bg-violet-50',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => {
        const inner = (
          <>
            <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${card.accent}`}>
              {card.icon}
            </span>
            <div>
              <p className="text-xl font-bold text-slate-800">{card.value}</p>
              <p className="text-sm text-slate-500">{card.label}</p>
            </div>
          </>
        );

        // Tarjeta informativa (Total por cobrar): no filtra, no es interactiva.
        if (card.filter === null) {
          return (
            <div
              key={card.key}
              className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-left"
            >
              {inner}
            </div>
          );
        }

        const isActive = activeFilter === card.filter;
        return (
          <button
            key={card.key}
            type="button"
            onClick={() => onFilterChange(card.filter as CobranzaFilter)}
            aria-pressed={isActive}
            className={`flex cursor-pointer flex-col gap-3 rounded-2xl border p-5 text-left transition-all ${
              isActive
                ? 'border-[#7C5CFC] bg-white shadow-md ring-2 ring-[#7C5CFC]/20'
                : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
            }`}
          >
            {inner}
          </button>
        );
      })}
    </div>
  );
};
